import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import { NoAccessScreen } from './NoAccessScreen'
import { SignedOutScreen } from './SignedOutScreen'
import { returnTo, signedOut } from './session'
import { useStaffMe } from './useStaff'
import { Wordmark } from '@/components'

/**
 * Wraps the ENTIRE authenticated tree, not individual routes.
 *
 * Because every page is `React.lazy`, a visitor who is not staff never
 * downloads a chunk that names a single screen — the bundle itself stops being
 * a description of what this tool does.
 *
 * `isError || !data?.is_staff` collapses to one screen on purpose:
 * `staffGuard.test.tsx` pins both directions, because a `?? true` in the wrong
 * place here is a one-character bug with total blast radius.
 */
/**
 * Force one auth screen, for review. DEVELOPMENT ONLY.
 *
 * ── WITHOUT THIS, THE NEW SCREENS CANNOT BE LOOKED AT ───────────────────────
 * Fixture mode — the only credential-free way to review this app — starts
 * `ready` and `authenticated` at `true` and returns before init, so `booting`,
 * `redirecting`, `checking`, the signed-out screen and the no-access screen are
 * all unreachable. The alternative was deliberately breaking a real Keycloak
 * session to see a splash, which nobody will do, which means those screens
 * would ship unreviewed.
 *
 * `import.meta.env.DEV` is replaced with the literal `false` by Vite in any
 * production build, so this whole branch is dead code the bundler drops — the
 * same mechanism the fixture adapter relies on, and `deploy.sh` greps the
 * output for that adapter's sentinel on every deploy.
 *
 *   ?authDemo=booting | redirecting | checking | signedOut | noAccess
 */
type AuthDemo = 'booting' | 'redirecting' | 'checking' | 'signedOut' | 'noAccess'

function useAuthDemo(): AuthDemo | null {
  const [params] = useSearchParams()
  if (!import.meta.env.DEV) return null
  const v = params.get('authDemo')
  return v === 'booting' || v === 'redirecting' || v === 'checking' || v === 'signedOut' || v === 'noAccess'
    ? v
    : null
}

export function StaffOnly({ children }: { children: ReactNode }) {
  const { ready, authenticated, login } = useAuth()
  const demo = useAuthDemo()
  const staff = useStaffMe()
  const navigate = useNavigate()
  // Read once, at mount: `login()` clears the flag, and re-reading storage on
  // every render would flip the screen out from under the button.
  const [wasSignedOut] = useState(() => signedOut.is())

  useEffect(() => {
    // The flag is what makes «Cerrar sesión» stick. Without it this effect
    // fires the moment Keycloak reports no session and signs the user straight
    // back in through SSO — sign-out that bounces you back reads as the app
    // refusing to let go.
    if (ready && !authenticated && !wasSignedOut) login()
  }, [ready, authenticated, login, wasSignedOut])

  /**
   * Put the user back where they were going.
   *
   * `AuthProvider` pins Keycloak's `redirectUri` to the app's origin — reading
   * `window.location.href` at call time is what produced "Invalid parameter:
   * redirect_uri" — so every login lands on `/`. Without this, a shared link to
   * `/orgs/acme?internal=include` always opens on Resumen for anybody who was
   * not already signed in, which is the one thing this tool exists to do.
   */
  useEffect(() => {
    if (!authenticated) return
    const to = returnTo.take()
    if (to) navigate(to, { replace: true })
  }, [authenticated, navigate])

  if (demo === 'noAccess') return <NoAccessScreen />
  if (demo === 'signedOut') return <SignedOutScreen onSignIn={() => {}} />
  if (demo) return <Splash phase={demo} />

  if (!ready) return <Splash phase="booting" />

  if (!authenticated) {
    return wasSignedOut ? (
      <SignedOutScreen
        onSignIn={() => {
          signedOut.clear()
          login()
        }}
      />
    ) : (
      // Not a fallthrough: the effect above is already navigating away.
      <Splash phase="redirecting" />
    )
  }

  if (staff.isPending) return <Splash phase="checking" />

  // Same screen for "no" and for "could not ask".
  if (staff.isError || !staff.data?.is_staff) return <NoAccessScreen />

  return <>{children}</>
}

/**
 * The three moments before the app appears.
 *
 * They were one bare 20px spinner. Separating them costs nothing and turns a
 * blank pause into a sentence — and the third one matters operationally: a slow
 * `/staff/me` is the difference between "the service is thinking" and "the
 * service is down", which is a distinction this app makes everywhere else.
 *
 * The copy says nothing a visitor could learn from. `redirecting` is the only
 * phase an unauthenticated stranger ever sees, and it names no product, no
 * surface and no reason.
 */
const PHASE_COPY = {
  booting: 'Un momento.',
  redirecting: 'Un momento.',
  checking: 'Comprobando el acceso…',
} as const

function Splash({ phase }: { phase: keyof typeof PHASE_COPY }) {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const t = window.setTimeout(() => setSlow(true), 4000)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
        <Wordmark size="lg" />
        <CircularProgress size={18} />
        <Typography variant="caption" color="text.secondary">
          {PHASE_COPY[phase]}
        </Typography>
        {slow && (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280 }}>
            Esto está tardando más de lo normal.
          </Typography>
        )}
      </Stack>
    </Box>
  )
}
